/**
 * La campagne — la tournée des comptoirs (lot 7 du plan).
 *
 * Huit comptoirs, du café de la gare au zinc du taulier : on y affronte les huit
 * niveaux dans l'ordre, un comptoir s'ouvre quand le précédent est gagné, et chacun
 * porte un **défi** facultatif qui se décroche en plus de la victoire.
 *
 * ## Ce que ce module n'est pas
 *
 * Comme le tournoi, ce n'est **pas** une variante de règles. Chaque comptoir est une
 * partie ordinaire — même moteur, mêmes combinaisons, mêmes tables d'IA, calibrées
 * sur un RuleSet précis (§ 3.4). Ce qui monte d'un comptoir à l'autre, c'est le
 * niveau des adversaires et le nombre de sièges, jamais les règles.
 *
 * ## Trois décisions de forme
 *
 * 1. **Le déblocage se déduit des victoires**, jamais d'un compteur « comptoir
 *    courant ». Sans quoi rejouer un vieux comptoir pour tenter son défi ferait
 *    reculer la campagne.
 * 2. **Un défi ne compte que sur une victoire.** « Sans rampeau » ou « au plus six
 *    jetons » se décrocheraient en perdant vite : les défis les plus durs seraient
 *    les plus faciles.
 * 3. **Une sauvegarde survit à un changement de contenu.** Le nombre de comptoirs
 *    bougera ; refuser une progression écrite avant un ajout effacerait la campagne
 *    du joueur à chaque mise à jour.
 */
import { additionner, statistiquesVides, } from "./statistiques.js";
/**
 * Le défi est-il rempli ?
 *
 * Une partie perdue ne remplit **aucun** défi, quel que soit le relevé : c'est la
 * règle qui empêche de décrocher « sans rampeau » en abandonnant tout de suite.
 */
export function defiReussi(defi, bilan) {
    if (!bilan.gagne)
        return false;
    const s = bilan.stats;
    switch (defi.type) {
        case "AVEC_421": return s.quatreCentVingtEtUn > 0;
        case "AVEC_SEC": return s.secs > 0;
        case "SANS_RAMPEAU": return s.rampeaux === 0;
        case "EN_MOINS_DE": return s.manches <= defi.manches;
        case "AU_PLUS": return s.jetonsRamasses <= defi.jetons;
    }
}
/**
 * La tournée, dans l'ordre où on la fait.
 *
 * La montée se joue sur **deux** axes, et pas seulement sur le niveau : la table
 * grandit aussi. À trois adversaires, poser un score moyen ne suffit plus, parce
 * qu'il y a trois occasions de faire pire que soi. Le dernier comptoir, lui, se joue
 * en soirée entière — trois parties d'affilée contre le haut du panier.
 *
 * ## Les défis sont mesurés, pas devinés
 *
 * « Six jetons au plus » ou « neuf manches » sont des réglages de contenu : rien dans
 * le code ne dira jamais qu'un nombre est hors d'atteinte ou offert. On les mesure
 * donc, avec un taulier à la place du joueur — le taux obtenu est un **plafond**, ce
 * que le meilleur joueur possible décroche (`v2/tools/mesurer-campagne.mjs`,
 * 400 parties par comptoir) :
 *
 * | # | victoire | étoile |   | # | victoire | étoile |
 * |--:|---------:|-------:|---|--:|---------:|-------:|
 * | 1 |     78 % |   30 % |   | 5 |     86 % |   33 % |
 * | 2 |     88 % |   46 % |   | 6 |     94 % |   48 % |
 * | 3 |     89 % |   78 % |   | 7 |     89 % |   28 % |
 * | 4 |     93 % |   42 % |   | 8 |     83 % |   47 % |
 *
 * Le comptoir 3 est volontairement au-dessus de la bande : la première étoile facile
 * sert à faire comprendre qu'il en existe.
 *
 * ⚠️ **« Au plus N jetons » s'affaiblit quand la table grandit** : gagner à cinq
 * implique déjà de n'avoir presque rien ramassé, et le défi devient une conséquence
 * de la victoire plutôt qu'une contrainte. Mesuré : à 4 jetons au Cercle, 75 %
 * d'étoiles. Les grandes tables demandent donc un défi d'une autre nature — une
 * borne de manches, ou un 421.
 */
export const COMPTOIRS = [
    {
        numero: 1,
        cle: "gare",
        adversaires: [1],
        soiree: false,
        defi: { type: "AU_PLUS", jetons: 6 },
    },
    {
        numero: 2,
        cle: "balto",
        adversaires: [2, 1],
        soiree: false,
        defi: { type: "AVEC_SEC" },
    },
    {
        numero: 3,
        cle: "petitZinc",
        adversaires: [3, 2],
        soiree: false,
        defi: { type: "SANS_RAMPEAU" },
    },
    {
        numero: 4,
        cle: "chope",
        adversaires: [4, 3, 2],
        soiree: false,
        defi: { type: "EN_MOINS_DE", manches: 9 },
    },
    {
        numero: 5,
        cle: "tabac",
        adversaires: [5, 4, 3],
        soiree: false,
        defi: { type: "AVEC_421" },
    },
    {
        numero: 6,
        cle: "cercle",
        adversaires: [6, 5, 4, 3],
        soiree: false,
        defi: { type: "EN_MOINS_DE", manches: 8 },
    },
    {
        numero: 7,
        cle: "arriereSalle",
        adversaires: [7, 6, 5, 4],
        soiree: false,
        defi: { type: "AVEC_421" },
    },
    {
        numero: 8,
        cle: "taulier",
        adversaires: [8, 7, 6],
        soiree: true,
        defi: { type: "AU_PLUS", jetons: 20 },
    },
];
export class ComptoirInconnu extends Error {
}
export class ComptoirFerme extends Error {
}
export class ProgressionIllisible extends Error {
}
export function comptoirDe(numero) {
    const comptoir = COMPTOIRS.find((c) => c.numero === numero);
    if (!comptoir)
        throw new ComptoirInconnu(`comptoir inconnu : ${numero}`);
    return comptoir;
}
/**
 * Qui s'installe à ce comptoir : le joueur, puis ses adversaires.
 *
 * Les adversaires portent le **nom de leur profil** — on affronte « Le taulier », pas
 * « IA niveau 8 ». Un adversaire nommé se retient et se déteste ; c'est toute la
 * différence entre une campagne et une suite de parties.
 *
 * ⚠️ Ces noms sont fournis par l'appelant (`nommer`), parce qu'ils sont **traduits**
 * et que ce module ne connaît aucune langue. Ils atterrissent dans l'ÉTAT de la
 * partie : une soirée commencée en français garde ses noms français, et c'est voulu —
 * on ne rebaptise pas quelqu'un au milieu d'une manche.
 */
export function placesDuComptoir(nomJoueur, comptoir, nommer, nomParDefaut = "Toi") {
    const nom = nomJoueur.replace(/\p{C}/gu, "").trim().slice(0, 16) || nomParDefaut;
    return [
        { nom, estIA: false, niveauIA: 0 },
        ...comptoir.adversaires.map((niveau) => ({
            nom: nommer(niveau), estIA: true, niveauIA: niveau,
        })),
    ];
}
const ETAPE_NEUVE = { gagnee: false, defiReussi: false, tentatives: 0 };
export function progressionNeuve() {
    return {
        etapes: COMPTOIRS.map(() => ETAPE_NEUVE),
        stats: statistiquesVides(),
    };
}
/**
 * Le comptoir est-il ouvert ?
 *
 * Le premier l'est toujours ; les autres le sont dès que le précédent est **gagné**.
 * Rien ici ne dépend d'un « où en est le joueur » : c'est ce qui permet de retourner
 * chercher une étoile sans rien perdre.
 */
export function comptoirOuvert(progression, numero) {
    if (numero <= 1)
        return numero === 1;
    return progression.etapes[numero - 2]?.gagnee ?? false;
}
/**
 * Enregistre une partie jouée à un comptoir.
 *
 * Ni la victoire ni l'étoile ne se reprennent : on ne fait qu'ajouter. Un joueur qui
 * revient tenter un défi ne risque donc jamais ce qu'il a déjà.
 */
export function enregistrer(progression, numero, bilan) {
    const comptoir = comptoirDe(numero);
    if (!comptoirOuvert(progression, numero)) {
        throw new ComptoirFerme(`le comptoir ${numero} n'est pas ouvert`);
    }
    const avant = progression.etapes[numero - 1] ?? ETAPE_NEUVE;
    const etapes = progression.etapes.map((etape, i) => (i === numero - 1
        ? {
            gagnee: avant.gagnee || bilan.gagne,
            defiReussi: avant.defiReussi || defiReussi(comptoir.defi, bilan),
            tentatives: avant.tentatives + 1,
        }
        : etape));
    return { etapes, stats: additionner(progression.stats, bilan.stats) };
}
export function avancement(progression) {
    const gagnes = progression.etapes.filter((e) => e.gagnee).length;
    const etoiles = progression.etapes.filter((e) => e.defiReussi).length;
    const prochain = COMPTOIRS.find((c) => !progression.etapes[c.numero - 1]?.gagnee
        && comptoirOuvert(progression, c.numero))?.numero ?? null;
    return {
        gagnes, etoiles, total: COMPTOIRS.length,
        termine: gagnes === COMPTOIRS.length,
        prochain,
    };
}
// ------------------------------------------------------- lire et écrire
/**
 * Version du format. À incrémenter seulement si la **forme** change — ajouter un
 * comptoir n'en est pas une, et ne doit rien invalider.
 */
export const VERSION_PROGRESSION = 1;
export function serialiserProgression(progression) {
    const enveloppe = {
        version: VERSION_PROGRESSION,
        etapes: progression.etapes,
        stats: progression.stats,
    };
    return JSON.stringify(enveloppe);
}
/**
 * Relit une progression.
 *
 * Sévère sur le format, **tolérante sur le contenu** : une version inconnue est
 * refusée (on ne devine pas une forme), mais un nombre de comptoirs différent est
 * simplement ajusté, et une étape abîmée repart à neuf sans emporter les autres.
 * Perdre une campagne parce qu'un champ manque serait le pire des refus.
 */
export function deserialiserProgression(brut) {
    let enveloppe;
    try {
        enveloppe = JSON.parse(brut);
    }
    catch {
        throw new ProgressionIllisible("progression illisible : JSON invalide");
    }
    if (!enveloppe || typeof enveloppe !== "object") {
        throw new ProgressionIllisible("progression illisible : enveloppe absente");
    }
    if (enveloppe.version !== VERSION_PROGRESSION) {
        throw new ProgressionIllisible(`progression en version ${String(enveloppe.version)}, `
            + `attendue ${VERSION_PROGRESSION}`);
    }
    const lues = Array.isArray(enveloppe.etapes) ? enveloppe.etapes : [];
    const etapes = COMPTOIRS.map((_, i) => etapeSaine(lues[i]));
    return { etapes, stats: statsSaines(enveloppe.stats) };
}
function etapeSaine(brute) {
    const e = brute;
    if (!e || typeof e !== "object")
        return ETAPE_NEUVE;
    return {
        gagnee: e.gagnee === true,
        defiReussi: e.defiReussi === true,
        tentatives: typeof e.tentatives === "number" && e.tentatives >= 0
            ? Math.floor(e.tentatives) : 0,
    };
}
function statsSaines(brutes) {
    const vides = statistiquesVides();
    const s = brutes;
    if (!s || typeof s !== "object")
        return vides;
    const out = { ...vides };
    for (const [cle, defaut] of Object.entries(vides)) {
        const valeur = s[cle];
        if (typeof defaut === "number") {
            out[cle] = typeof valeur === "number" && Number.isFinite(valeur) ? valeur : defaut;
        }
        else {
            out[cle] = typeof valeur === "string" ? valeur : defaut;
        }
    }
    return out;
}
//# sourceMappingURL=campagne.js.map